/**
 * Inking sidebar panel for Inking mode controls.
 *
 * Render mode no longer hosts inking controls; all inking setup and
 * bake/export actions live in Inking mode.
 */

import { useCallback, useRef, useState } from "react";
import { DEFAULT_INKING_SETTINGS } from "../../app/inkingDefaults.ts";
import { useStore } from "../../app/store.ts";
import type { InkingBakePhase } from "../../app/storeTypes.ts";
import { Button } from "../Button.tsx";
import { ColorWheel } from "../ColorWheel.tsx";
import { Popover } from "../Popover.tsx";
import { ProgressBar } from "../ProgressBar.tsx";
import { LabeledRow, SectionLabel, SliderRow, Toggle } from "../shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_BODY,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "../tokens.ts";

const BAKE_PHASE_LABELS: Record<InkingBakePhase, string> = {
  init: "Initialising",
  features: "Extracting features",
  strokes: "Building strokes",
};

// ---------------------------------------------------------------------------
// InkColorRow — swatch + popover color picker for the baked stroke color.
// Mirrors PaletteColorWell (ToolPalette.tsx) adapted to a panel label row.
// ---------------------------------------------------------------------------

function InkColorRow({
  color,
  onChange,
}: {
  color: string;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  return (
    <LabeledRow label="Color">
      <button
        ref={btnRef}
        onClick={() => setOpen((p) => !p)}
        title={`Ink color: ${color}`}
        style={{
          width: 24,
          height: 24,
          borderRadius: RADIUS_SM,
          border: open
            ? `1.5px solid ${COLOR_ACCENT}`
            : `1.5px solid ${COLOR_BORDER_STRONG}`,
          background: color,
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)",
          transition: "border-color 0.15s ease",
        }}
      />
      <span
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_SUBTLE,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {color.toUpperCase()}
      </span>
      <Popover
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        style={{
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <ColorWheel value={color} onChange={onChange} size={140} />
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_SUBTLE,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {color.toUpperCase()}
        </span>
      </Popover>
    </LabeledRow>
  );
}

// ---------------------------------------------------------------------------
// RenderInkingPanelBody
// ---------------------------------------------------------------------------

export function RenderInkingPanelBody() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const isInkingMode = workspaceMode === "inking";

  const inkingThickness = useStore((s) => s.inkingThickness);
  const setInkingThickness = useStore((s) => s.setInkingThickness);
  const inkingThicknessMin = useStore((s) => s.inkingThicknessMin);
  const setInkingThicknessMin = useStore((s) => s.setInkingThicknessMin);
  const inkingShadowResponse = useStore((s) => s.inkingShadowResponse);
  const setInkingShadowResponse = useStore((s) => s.setInkingShadowResponse);
  const inkingShadowExposure = useStore((s) => s.inkingShadowExposure);
  const setInkingShadowExposure = useStore((s) => s.setInkingShadowExposure);
  const inkingLineWidthVariation = useStore((s) => s.inkingLineWidthVariation);
  const setInkingLineWidthVariation = useStore(
    (s) => s.setInkingLineWidthVariation,
  );
  const inkingColor = useStore((s) => s.inkingColor);
  const setInkingColor = useStore((s) => s.setInkingColor);
  const inkingOpacity = useStore((s) => s.inkingOpacity);
  const setInkingOpacity = useStore((s) => s.setInkingOpacity);
  const includePrimitives = useStore((s) => s.inkingIncludePrimitives);
  const setIncludePrimitives = useStore((s) => s.setInkingIncludePrimitives);
  const includeMannequins = useStore((s) => s.inkingIncludeMannequins);
  const setIncludeMannequins = useStore((s) => s.setInkingIncludeMannequins);
  const includeBlendObjects = useStore((s) => s.inkingIncludeBlendObjects);
  const setIncludeBlendObjects = useStore(
    (s) => s.setInkingIncludeBlendObjects,
  );
  const inkingDistanceNear = useStore((s) => s.inkingDistanceNear);
  const setInkingDistanceNear = useStore((s) => s.setInkingDistanceNear);
  const inkingDistanceFar = useStore((s) => s.inkingDistanceFar);
  const setInkingDistanceFar = useStore((s) => s.setInkingDistanceFar);
  const hatchingEnabled = useStore((s) => s.inkingHatchingEnabled);
  const setHatchingEnabled = useStore((s) => s.setInkingHatchingEnabled);
  const hatchingDirection = useStore((s) => s.inkingHatchingDirection);
  const setHatchingDirection = useStore((s) => s.setInkingHatchingDirection);
  const hatchingExposure = useStore((s) => s.inkingHatchingExposure);
  const setHatchingExposure = useStore((s) => s.setInkingHatchingExposure);
  const hatchingDensity = useStore((s) => s.inkingHatchingDensity);
  const setHatchingDensity = useStore((s) => s.setInkingHatchingDensity);
  const hatchingJitter = useStore((s) => s.inkingHatchingJitter);
  const setHatchingJitter = useStore((s) => s.setInkingHatchingJitter);

  const baking = useStore((s) => s.inkingMode?.baking ?? false);
  const baked = useStore((s) => s.inkingMode?.baked ?? false);
  const bakeProgress = useStore((s) => s.inkingMode?.bakeProgress ?? 0);
  const bakePhase: InkingBakePhase = useStore(
    (s) => s.inkingMode?.bakePhase ?? "init",
  );
  const autoGenerate = useStore((s) => s.inkingAutoGenerate);
  const setAutoGenerate = useStore((s) => s.setInkingAutoGenerate);

  const handleGenerate = useCallback(() => {
    import("../../viewport/inking/InkingOverlay.tsx").then(
      ({ triggerInkingBake }) => {
        triggerInkingBake();
      },
    );
  }, []);

  const handleCancel = useCallback(() => {
    import("../../viewport/inking/InkingOverlay.tsx").then(
      ({ cancelInkingBake }) => {
        cancelInkingBake();
      },
    );
  }, []);

  const handleExportPng = useCallback(() => {
    import("../../viewport/inking/InkingOverlay.tsx").then(
      ({ triggerInkingExport }) => {
        void triggerInkingExport();
      },
    );
  }, []);

  return (
    <>
      {/* Generate / Cancel / Export — only in inking mode, prominent at top */}
      {isInkingMode && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            {baking ? (
              <Button
                onClick={handleCancel}
                variant="secondary"
                size="md"
                fullWidth
                title="Stop the running bake at the next phase boundary"
              >
                Cancel
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                variant="primary"
                size="md"
                fullWidth
              >
                {baked ? "Regenerate Strokes" : "Generate Strokes"}
              </Button>
            )}
            <Button
              onClick={handleExportPng}
              disabled={baking || !baked}
              title="Export the current inking preview as a transparent PNG at the configured output size"
              variant="secondary"
              size="md"
            >
              Export PNG
            </Button>
          </div>
          {baking && (
            <ProgressBar
              value={bakeProgress}
              label={`${BAKE_PHASE_LABELS[bakePhase]}…`}
              ariaLabel={`Inking bake — ${BAKE_PHASE_LABELS[bakePhase]}`}
              style={{ padding: "6px 0 2px" }}
            />
          )}
          <Toggle
            label="Auto-generate on camera change"
            checked={autoGenerate}
            onChange={setAutoGenerate}
          />
        </div>
      )}

      <SectionLabel>Include</SectionLabel>
      <Toggle
        label="Primitives"
        checked={includePrimitives}
        onChange={setIncludePrimitives}
      />
      <Toggle
        label="Mannequins"
        checked={includeMannequins}
        onChange={setIncludeMannequins}
      />
      <Toggle
        label="Blender Objects"
        checked={includeBlendObjects}
        onChange={setIncludeBlendObjects}
      />

      <SectionLabel>Line Style</SectionLabel>
      <InkColorRow color={inkingColor} onChange={setInkingColor} />
      <SliderRow
        label="Max Thickness"
        value={inkingThickness}
        min={0.5}
        max={8}
        step={0.1}
        displayValue={`${inkingThickness.toFixed(1)} px`}
        onChange={setInkingThickness}
        defaultValue={DEFAULT_INKING_SETTINGS.thickness}
      />
      <SliderRow
        label="Min Thickness"
        value={inkingThicknessMin}
        min={0}
        max={8}
        step={0.1}
        displayValue={`${inkingThicknessMin.toFixed(1)} px`}
        onChange={setInkingThicknessMin}
        defaultValue={DEFAULT_INKING_SETTINGS.thicknessMin}
      />
      <SliderRow
        label="Light-to-Width"
        value={inkingShadowResponse}
        min={0}
        max={1}
        step={0.05}
        displayValue={`${Math.round(inkingShadowResponse * 100)}%`}
        onChange={setInkingShadowResponse}
        defaultValue={DEFAULT_INKING_SETTINGS.shadowResponse}
      />
      <SliderRow
        label="Light Bias"
        value={inkingShadowExposure}
        min={0}
        max={1}
        step={0.05}
        displayValue={`${Math.round(inkingShadowExposure * 100)}%`}
        onChange={setInkingShadowExposure}
        defaultValue={DEFAULT_INKING_SETTINGS.shadowExposure}
      />
      <SliderRow
        label="Near Distance"
        value={inkingDistanceNear}
        min={0}
        max={20}
        step={0.1}
        displayValue={`${inkingDistanceNear.toFixed(1)} u`}
        onChange={setInkingDistanceNear}
        defaultValue={DEFAULT_INKING_SETTINGS.distanceNear}
      />
      <SliderRow
        label="Far Distance"
        value={inkingDistanceFar}
        min={1}
        max={50}
        step={1}
        displayValue={`${inkingDistanceFar.toFixed(0)} u`}
        onChange={setInkingDistanceFar}
        defaultValue={DEFAULT_INKING_SETTINGS.distanceFar}
      />
      <SliderRow
        label="Silhouette Boost"
        value={inkingLineWidthVariation}
        min={0}
        max={1}
        step={0.05}
        displayValue={`${Math.round(inkingLineWidthVariation * 100)}%`}
        onChange={setInkingLineWidthVariation}
        defaultValue={DEFAULT_INKING_SETTINGS.lineWidthVariation}
      />
      <SliderRow
        label="Opacity"
        value={inkingOpacity}
        min={0}
        max={1}
        step={0.05}
        displayValue={`${Math.round(inkingOpacity * 100)}%`}
        onChange={setInkingOpacity}
        defaultValue={DEFAULT_INKING_SETTINGS.opacity}
      />
      <Toggle
        label="Hatching"
        checked={hatchingEnabled}
        onChange={setHatchingEnabled}
      />
      {hatchingEnabled && (
        <>
          <SliderRow
            label="Direction"
            value={hatchingDirection}
            min={0}
            max={180}
            step={5}
            displayValue={`${Math.round(hatchingDirection)}°`}
            onChange={setHatchingDirection}
            defaultValue={DEFAULT_INKING_SETTINGS.hatchingDirection}
          />
          <SliderRow
            label="Light Bias"
            value={hatchingExposure}
            min={0}
            max={1.5}
            step={0.05}
            displayValue={`${Math.round(hatchingExposure * 100)}%`}
            onChange={setHatchingExposure}
            defaultValue={DEFAULT_INKING_SETTINGS.hatchingExposure}
          />
          <SliderRow
            label="Density"
            value={hatchingDensity}
            min={0}
            max={1.25}
            step={0.05}
            displayValue={`${Math.round(hatchingDensity * 100)}%`}
            onChange={setHatchingDensity}
            defaultValue={DEFAULT_INKING_SETTINGS.hatchingDensity}
          />
          <SliderRow
            label="Jitter"
            value={hatchingJitter}
            min={0}
            max={1}
            step={0.05}
            displayValue={`${Math.round(hatchingJitter * 100)}%`}
            onChange={setHatchingJitter}
            defaultValue={DEFAULT_INKING_SETTINGS.hatchingJitter}
          />
        </>
      )}
    </>
  );
}
